import dayjs from 'dayjs';

import {
  FormControlLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import 'dayjs/locale/en';

import CustomCheckbox from '@shared/components/forms/theme-elements/CustomCheckbox';
import { useStyles } from './styles';
import { CallScheduleRequestDto } from '@shared/services/apiService/apiService';
import { useFormContext } from 'react-hook-form';
import { CreateCampaignFormValue } from '@features/Admin/Campaign/model/type';
import CustomSelect from '@shared/components/forms/theme-elements/CustomSelect';
import { IconChevronDown } from '@shared/assets/images/svgs';
import { t } from 'i18next';
import { keys } from 'lodash';
import { ChangeEvent, useEffect } from 'react';
import { RootState, useAppSelector } from '@shared/store';
import { UserRolesEnum } from '@shared/constants/auth.constants';

const week = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

const timezones = {
  'Asia/Dubai': '(GMT+4)',
  'Europe/Berlin': '(GMT+1)',
};

type weekDay = (typeof week)[number];

interface ScheduleSelectProps {
  schedule: CallScheduleRequestDto;
  setSchedule: (value: CallScheduleRequestDto) => void;
}

export const ScheduleSelect: React.FC<ScheduleSelectProps> = ({
  schedule,
  setSchedule,
}: ScheduleSelectProps) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const {
    formState: { errors: formStateErrors },
    setError,
    clearErrors,
  } = useFormContext<CreateCampaignFormValue>();
  const currentUser = useAppSelector((state: RootState) => state.auth.authenticatedUser);

  const getTimeFromSchedule = (day: string, type: 'Start' | 'End', extended: boolean = false) => {
    const dayKey = day.toLowerCase() as keyof CallScheduleRequestDto;
    const key = type === 'Start' ? `${dayKey}Start` : `${dayKey}End`;

    if (key in schedule) {
      return schedule[key as keyof CallScheduleRequestDto];
    }

    return extended ? '09:00' : '9:00:00';
  };

  const handleTimeChange = (day: string, period: 'Start' | 'End', newValue: any) => {
    setSchedule({
      ...schedule,
      [`${day.toLowerCase()}${period}` as keyof CallScheduleRequestDto]: newValue
        ? newValue.format('HH:mm:ss')
        : null,
    });
  };

  const handleChangeTimezone = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (event && event.target && event.target.value) {
      const actualTimezone = event.target.value;

      setSchedule({
        ...schedule,
        timezone: actualTimezone,
      });
    }
  };

  const validateSchedule = (schedule: CallScheduleRequestDto) => {
    const isScheduleValid = keys(schedule).some(
      (key) => key !== 'timezone' && schedule[key as keyof CallScheduleRequestDto] !== null,
    );
    if (!isScheduleValid) {
      setError('callSchedule', { type: 'required' });
    } else if (Boolean(formStateErrors.callSchedule)) {
      clearErrors('callSchedule');
    }
  };

  const handleToggleSelectedDay = (event: ChangeEvent<HTMLInputElement>, day: weekDay) => {
    const isChecked = event.target.checked;
    const newSchedule = {
      ...schedule,
      [`${day.toLowerCase()}Start`]: isChecked
        ? getTimeFromSchedule(day, 'Start') || '09:00:00'
        : null,
      [`${day.toLowerCase()}End`]: isChecked ? getTimeFromSchedule(day, 'End') || '18:00:00' : null,
    };
    setSchedule(newSchedule);
    validateSchedule(newSchedule);
  };

  useEffect(() => {
    validateSchedule(schedule);
  }, [schedule]);

  return (
    <>
      <TableContainer style={{ width: '100%' }}>
        <Table
          className={classes.tableV2}
          sx={{
            ...(formStateErrors.callSchedule
              ? {
                  borderColor: `${theme.palette.error.main} !important`,
                }
              : {}),
          }}>
          <TableHead>
            <TableRow>
              <TableCell
                className={classes.table_header_cellV2}
                sx={{
                  paddingLeft: '16px!important',
                }}>
                <Typography className={classes.columnNameV2} variant="h6">
                  Week
                </Typography>
              </TableCell>
              <TableCell className={classes.table_header_cellV2}>
                <Typography className={classes.columnNameV2} variant="h6">
                  From
                </Typography>
              </TableCell>
              <TableCell className={classes.table_header_cellV2}>
                <Typography className={classes.columnNameV2} variant="h6">
                  To
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {week.map((day: weekDay) => {
              const isDayActive =
                Boolean(schedule[`${day.toLowerCase()}Start` as keyof CallScheduleRequestDto]) &&
                Boolean(schedule[`${day.toLowerCase()}End` as keyof CallScheduleRequestDto]);
              const isViewer = currentUser?.role === UserRolesEnum.VIEWER;
              const isTimeDisabled = isViewer || !isDayActive;

              return (
                <TableRow key={day}>
                  <TableCell className={classes.table_row_cellV2}>
                    <FormControlLabel
                      className={isDayActive ? classes.weekdayLabel : classes.weekdayLabelInactive}
                      control={
                        <CustomCheckbox
                          checked={isDayActive}
                          disabled={isViewer}
                          highlighted={Boolean(formStateErrors.callSchedule)}
                          onChange={(event) => handleToggleSelectedDay(event, day)}
                        />
                      }
                      label={day}
                    />
                  </TableCell>
                  <TableCell className={classes.table_row_middle_cellV2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
                      <DemoContainer components={['TimePicker']} sx={{ pt: 0 }}>
                        <DesktopTimePicker
                          sx={{ width: '100%' }}
                          className={classes.timePickerInputV2}
                          format="HH:mm"
                          disabled={isTimeDisabled}
                          value={dayjs(
                            `2022-04-17T${getTimeFromSchedule(day, 'Start') || '09:00'}`,
                          )}
                          onChange={(newValue) => {
                            return handleTimeChange(day, 'Start', newValue);
                          }}
                        />
                      </DemoContainer>
                    </LocalizationProvider>
                  </TableCell>
                  <TableCell className={classes.table_row_last_cellV2} sx={{}}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
                      <DemoContainer components={['TimePicker']} sx={{ pt: 0 }}>
                        <DesktopTimePicker
                          sx={{ width: '100%' }}
                          className={classes.timePickerInputV2}
                          format="HH:mm"
                          disabled={isTimeDisabled}
                          value={dayjs(`2022-04-17T${getTimeFromSchedule(day, 'End') || '18:00'}`)}
                          onChange={(newValue) => {
                            return handleTimeChange(day, 'End', newValue);
                          }}
                        />
                      </DemoContainer>
                    </LocalizationProvider>
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell className={classes.timezoneLabelCell}>
                <Typography>{t('campaignPage.timezone')}:</Typography>
              </TableCell>
              <TableCell colSpan={2} className={classes.timezoneValueCell}>
                <CustomSelect
                  labelId="schedule-timezone"
                  name="scheduleTimezone"
                  onChange={handleChangeTimezone}
                  id="campaign-type-id"
                  disabled={currentUser?.role === UserRolesEnum.VIEWER}
                  value={
                    Object.keys(timezones).find((tz) =>
                      tz.startsWith(schedule.timezone || 'Asia/Dubai'),
                    ) || Object.keys(timezones)[0]
                  }
                  IconComponent={IconChevronDown}
                  className={classes.call_schedule_timezone_select}>
                  {Object.entries(timezones).map(([timezoneName, timezoneValue]) => {
                    return (
                      <MenuItem key={timezoneName} value={timezoneName}>
                        {timezoneName} {timezoneValue}
                      </MenuItem>
                    );
                  })}
                </CustomSelect>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
